import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  readonly label?: ReactNode;
  readonly invalid?: boolean;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { label, invalid, className, disabled, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        'inline-flex items-center gap-2 cursor-pointer select-none text-ink',
        'text-body leading-snug [@media(pointer:coarse)]:min-h-11',
        disabled && 'opacity-45 cursor-not-allowed',
        className,
      )}
    >
      <span className="relative inline-flex items-center justify-center">
        <input
          ref={ref}
          type="radio"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={cn(
            'peer appearance-none size-4 [@media(pointer:coarse)]:size-5 rounded-full bg-surface',
            'border border-ink-3/80',
            'transition-[background-color,border-color,box-shadow] duration-(--duration-tap) motion-reduce:transition-none',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text',
            'checked:border-accent-text',
            'disabled:cursor-not-allowed',
            invalid && 'border-danger focus-visible:ring-danger/25',
          )}
          {...rest}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute size-2 rounded-full bg-accent-text opacity-0 peer-checked:opacity-100"
        />
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
});
